import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import {
  incrementItemQuantity,
  changeItemForceInHands,
} from '@/checkout-instore-legacy/cart/actions'

import { BuyButton, BuyButtonProps } from './BuyButton'
import useNetwork from '@/checkout-instore-legacy/hooks/useNetwork'
import { Attachment } from '@/checkout-instore-legacy/product/typings'
import {
  endCatalogStepEvent,
  logClickEvent,
} from '@/checkout-instore-legacy/utils/EventHelper'
import { WithRouterProps } from 'react-router'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import isEmpty from 'lodash/isEmpty'
import { useAttachmentsContext } from '@/checkout-instore-legacy/components/Attachments/context/AttachmentsContext'

type Item = {
  id: string
  quantity: number
  manualPrice: number | null
}

export type BuyButtonContainerProps = {
  quantity: number
  manualPrice: number | null
  attachments?: Attachment[]
  beforeAddToCart?: () => void
} & Omit<BuyButtonProps, 'ref'>

const BuyButtonContainer = ({
  skuId,
  quantity,
  manualPrice,
  withoutStock,
  attachments,
  location,
  router,
  beforeAddToCart,
  children,
  ...props
}: // TODO: this error popped up when moving the checkout-instore project
// to checkout-instore-legacy, but needs to be fixed at some point
// @ts-ignore
BuyButtonContainerProps & WithRouterProps) => {
  const dispatch = useDispatch()
  const { enableAttachments } = getInstoreConfig()
  const { setAttachmentState, setShowAttachments } = useAttachmentsContext()

  // Remove assembly options from list
  const attachmentsFiels = attachments?.filter(
    // TODO: this error popped up when moving the checkout-instore project
    // to checkout-instore-legacy, but needs to be fixed at some point
    // @ts-ignore
    (attachments) => !attachments.composition
  )

  const showAttachments = useMemo(
    () => !isEmpty(attachmentsFiels) && enableAttachments,
    [enableAttachments, attachmentsFiels]
  )

  const addCurrentProductToCart = useCallback(() => {
    const item = {
      id: skuId,
      quantity,
      manualPrice,
    }

    const query = location.query

    logClickEvent('add-product-manually', 'Sale')
    endCatalogStepEvent()

    if (withoutStock) {
      dispatch(changeItemForceInHands(item as Item))
    } else {
      dispatch(incrementItemQuantity(item as Item, quantity, query))
    }
  }, [dispatch, manualPrice, quantity, skuId, withoutStock, location.query])

  const handleAddToCart = useCallback(() => {
    beforeAddToCart?.()

    addCurrentProductToCart()
  }, [addCurrentProductToCart, beforeAddToCart])

  const hasAssemblyItems = attachments?.some(
    // TODO: this error popped up when moving the checkout-instore project
    // to checkout-instore-legacy, but needs to be fixed at some point
    // @ts-ignore
    (attachment) => attachment.composition
  )

  const handleClick = useCallback(() => {
    if (showAttachments) {
      setAttachmentState({
        attachments,
        skuId,
        manualPrice,
        quantity,
        beforeAddToCart: () => {
          setShowAttachments(false)

          beforeAddToCart?.()

          if (hasAssemblyItems) {
            router.replace(`/checkout/services?skuId=${skuId}`)
          }
        },
      })
    } else if (hasAssemblyItems && !showAttachments) {
      router.push(`/checkout/services?skuId=${skuId}`)
    } else {
      handleAddToCart()
    }
  }, [
    router,
    hasAssemblyItems,
    attachments,
    handleAddToCart,
    manualPrice,
    quantity,
    setAttachmentState,
    showAttachments,
    skuId,
    beforeAddToCart,
    setShowAttachments,
  ])

  return (
    <BuyButton
      skuId={skuId}
      withoutStock={withoutStock}
      handleClick={handleClick}
      isOnline={useNetwork()}
      {...props}
    >
      {children}
    </BuyButton>
  )
}

export default withRouter(BuyButtonContainer)
